/* eslint-disable max-len */
/* eslint-disable operator-linebreak */
import { ActivityCouponState, CouponType, ExchangeState, CouponState } from '@tencent/wphk-act-base-types';
import { UserCouponTransState, UserSPCouponTransState } from '@tencent/wphk-act-base-cgi';
import dayjs from 'dayjs';
import { getServerTime } from '@tencent/wphk-components-common/utils/server-time';
export function transformCouponsV2(couponList, couponStateSort) {
    // 检查传入的couponStateSort格式
    if (!checkCouponStateSort(couponStateSort)) {
        throw new Error('couponStateSort传入格式错误，以下状态三选一：ActivityCouponState.received，ActivityCouponState.monthUserLimit，ActivityCouponState.totalUserLmit');
    }
    return couponList.map((coupon) => (Object.assign(Object.assign(Object.assign(Object.assign({}, coupon), { 
        // 库存
        availablePercent: getAvailablePercent(coupon) }), getCouponState({ coupon, couponStateSort })), { 
        // 时间
        curSysTime: getServerTime() })));
}
/** 检查券状态排序输入 */
function checkCouponStateSort(couponStateSort) {
    let flag = 0;
    for (const state of couponStateSort) {
        if (state === ActivityCouponState.received ||
            state === ActivityCouponState.monthUserLimit ||
            state === ActivityCouponState.totalUserLmit) {
            flag += 1;
            continue;
        }
    }
    return flag <= 1;
}
/**
 * 获取优惠券余量百分比
 */
function getAvailablePercent(coupon) {
    // totalNum 兑换活动最大参与总次数（所有用户）
    // totalSendNum 兑换活动已参与总次数（所有用户）
    const { totalNum, totalSendNum } = coupon;
    // 平台当日兑换上限次数
    // 平台当日已兑换次数
    const { dayLimit, daySent } = formatDayLimitSent(coupon);
    if (isDayLimitCoupon(coupon)) {
        // 有日限额的券
        return formatPercent(daySent, dayLimit);
    }
    // 没有日限额的券
    return formatPercent(totalSendNum, totalNum);
}
/** 生成百分比 */
const formatPercent = (sent, total) => {
    const result = ((total - sent) / total) * 100;
    return String(Math.min(100, Math.max(1, result))).split('.')[0];
};
/** 格式化日限额 */
const formatDayLimitSent = (coupon) => ({
    dayLimit: coupon.dayLimit || 99999999,
    daySent: coupon.daySent || 0,
});
/** 是否是日限额的券 */
function isDayLimitCoupon(coupon) {
    const { dayLimit, daySent } = formatDayLimitSent(coupon);
    return !(dayLimit === 99999999 && daySent === 0);
}
/** 是否是CP券 */
export function isCpType(coupon) {
    // 因为userCouponInfo的 couponId 为加密串，所以没有办法判断是 CP 还是 SP；
    // 所以这里认为 1、没有 couponType 则认为是 CP 券，否则认为是 SP 券；2、有 couponType 且 couponType 非展示券（线上日志有 couponType 为 1 的 CP 券）
    return (!(coupon === null || coupon === void 0 ? void 0 : coupon.couponType) ||
        ![CouponType.SPCOUOPN, CouponType.SPCOUOPN_CDKEY, CouponType.SPCOUOPN_ONECDKEY].includes(coupon.couponType));
}
/** CP、SP 的 transtate 是否为已过期 */
function isTranStateExpired(coupon) {
    if (!(coupon === null || coupon === void 0 ? void 0 : coupon.transState))
        return false;
    // 如果是 CP 券
    if (isCpType(coupon)) {
        return [UserCouponTransState.EXPIRED].includes(coupon.transState);
    }
    return [UserSPCouponTransState.EXPIRED].includes(coupon.transState);
}
/** CP、SP 的 transtate 是否为已使用 */
function isTranStateUsed(coupon) {
    if (!(coupon === null || coupon === void 0 ? void 0 : coupon.transState))
        return false;
    // 如果是 CP 券
    if (isCpType(coupon)) {
        return [UserCouponTransState.USED, UserCouponTransState.REFUND].includes(coupon.transState);
    }
    return [UserSPCouponTransState.USED, UserSPCouponTransState.OPENED].includes(coupon.transState);
}
/**
 * 处理券状态
 * @param coupon 券
 * @param couponStateSort 单券状态排序
 * @param lockInfo 时间锁信息
 * @returns
 */
export function getCouponState({ coupon, couponStateSort, lockInfo, }) {
    const isReceived = () => coupon.hasGot >= coupon.userLimit || (coupon.userMonthSent || 0) >= (coupon.userMonthLimit || 99999999);
    const isNotReceived = () => coupon.hasGot < coupon.userLimit && (coupon.userMonthSent || 0) < (coupon.userMonthLimit || 99999999);
    const stateCheckFunctions = {
        // 已过期，用户已领的券已经过期，且券没有回滚才被认为是真正的已过期
        [ActivityCouponState.expired]: () => coupon.hasGot >= coupon.userLimit && isTranStateExpired(coupon),
        // 已兑罄(奖品维度-总限额)
        [ActivityCouponState.totalLimit]: () => coupon.totalSendNum >= coupon.totalNum,
        // 今日已领完(奖品维度-日限额)
        [ActivityCouponState.dayLimit]: () => (coupon.daySent || 0) >= (coupon.dayLimit || 99999999),
        // 已领取
        [ActivityCouponState.received]: isReceived,
        // 月用户限额
        [ActivityCouponState.monthUserLimit]: isReceived,
        // 总用户限额
        [ActivityCouponState.totalUserLmit]: isReceived,
        // 已使用
        [ActivityCouponState.used]: () => isTranStateUsed(coupon),
        // [ActivityCouponState.qualNoEnough]: () => coupon.qualCnt > (coupon.stampRemainCnt || 0),
        // 未开始（显示锁）
        [ActivityCouponState.notStarted]: () => (lockInfo === null || lockInfo === void 0 ? void 0 : lockInfo.isShowLock) && dayjs(getServerTime()).isAfter(lockInfo.lockStartTime) && dayjs(getServerTime()).isBefore(lockInfo.lockEndTime),
        // 可兑换
        [ActivityCouponState.avaliable]: isNotReceived,
        // 未领取
        [ActivityCouponState.notReceived]: isNotReceived,
    };
    const stateResultMapping = {
        [ActivityCouponState.expired]: { couponState: CouponState.expired },
        [ActivityCouponState.totalLimit]: { exchangeState: ExchangeState.totalLimit },
        [ActivityCouponState.dayLimit]: { exchangeState: ExchangeState.dayLimit },
        [ActivityCouponState.received]: { couponState: CouponState.received },
        [ActivityCouponState.monthUserLimit]: { exchangeState: ExchangeState.monthUserLimit },
        [ActivityCouponState.totalUserLmit]: { exchangeState: ExchangeState.totalUserLmit },
        [ActivityCouponState.used]: { couponState: CouponState.used },
        // [ActivityCouponState.qualNoEnough]: { exchangeState: ExchangeState.qualNoEnough },
        [ActivityCouponState.notStarted]: { exchangeState: ExchangeState.notStarted },
        [ActivityCouponState.avaliable]: { exchangeState: ExchangeState.avaliable },
        [ActivityCouponState.notReceived]: { couponState: CouponState.notReceived },
    };
    for (const state of couponStateSort) {
        const checkFunction = stateCheckFunctions[state];
        if (checkFunction === null || checkFunction === void 0 ? void 0 : checkFunction()) {
            return stateResultMapping[state];
        }
    }
    if (couponStateSort.includes(ActivityCouponState.avaliable)) {
        return { exchangeState: ExchangeState.avaliable };
    }
    return { couponState: CouponState.notReceived };
}
